import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { BehaviorSubject, combineLatest, debounceTime, filter, map, Subject, Subscription, TeardownLogic } from 'rxjs';

import { ApplicantService } from 'services/applicant/applicant.service';
import { UserService } from 'services/user/user.service';
import { UserInfo } from 'services/user/user.service.class';

import { AllMenuItems } from './menus';
import { RouteMenus, RoutePaths } from 'routes/route.roles';
import { SystemRole } from 'modules/shared/roles';
import {
    ApplicationStatus,
    ApplicationStep,
    NonTeacherTransferSteps,
    TeacherTransferSteps,
} from 'services/applicant/applicant.service.class';

@Injectable({
    providedIn: 'root',
})
export class NavMenuService implements OnDestroy {
    private _subrelease = new Subscription();
    set subrelease(sub: TeardownLogic) {
        this._subrelease.add(sub);
    }

    private _MenuItems = new BehaviorSubject<NavMenuItem[]>([]);
    get MenuItems() {
        return this._MenuItems.asObservable();
    }

    private _NextStep = new BehaviorSubject<NavMenuItem | null>(null);
    get NextStep() {
        return this._NextStep.asObservable();
    }

    ApplicationStatus: ApplicationStatus | null = null;
    CurrentMenuItems: NavMenuItem[] = [];
    Overrides: Record<number, boolean> = {};
    RouteURL = '';
    User: UserInfo | null = null;

    requestUpdate = new Subject<void>();

    constructor(private appSvc: ApplicantService, private router: Router, private userSvc: UserService) {
        // Monitor requests for updates, and release on a cooldown cycle as needed.
        this.subrelease = this.requestUpdate.pipe(debounceTime(100)).subscribe(() => {
            // Process the updates.
            this.ProcessMenuItems();
        });

        this.subrelease = combineLatest([this.userSvc.UserSubject, this.appSvc.ApplicationStatus]).subscribe((info) => {
            this.User = info[0];
            this.ApplicationStatus = info[1];

            // Process user specific tool removals.
            // 1: PCPS Connect: Only allow option if the personnel group is '0' (unassigned/unknown).
            this.Overrides[RouteMenus.PCPSConnect] = this.User?.PersonnelGroup === '0';

            // Request an update to take place.
            this.requestUpdate.next();
        });

        // Hook into the router event structure so we can monitor successful route changes in order to
        // update the active button (and also remove buttons which may not apply).
        this.subrelease = this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map((event) => {
                    const navev: NavigationEnd = <NavigationEnd>event;
                    return navev.url;
                })
            )
            .subscribe((url) => {
                // Strip off any trailing pieces of the url path
                const urlroot = url.match(/^(\/.*?)(\/.*)?$/);
                if (urlroot && urlroot.length >= 2) {
                    url = urlroot[1];
                }

                // Strip off a leading slash (if any).
                if (url[0] === '/') {
                    url = url.substring(1);
                }

                if (this.RouteURL !== url) {
                    // We have a route change. Update the value, then request a menu update.
                    this.RouteURL = url;

                    this.requestUpdate.next();
                }
            });

        // Initialize the menu items.
        this.requestUpdate.next();
    }

    ngOnDestroy(): void {
        this._subrelease?.unsubscribe();
        this._MenuItems.complete();
    }

    // #region Disable Menu Item

    DisableMenuItem(itemID: RouteMenus) {
        if (itemID == null) return;

        // Set an override for this item to off/disabled.
        this.Overrides[itemID] = false;

        // Request update of menu items.
        this.requestUpdate.next();
    }

    // #endregion

    // #region Enable Menu Item

    EnableMenuItem(itemID: RouteMenus) {
        if (itemID == null) return;

        // Set an override for this item to on/enabled.
        this.Overrides[itemID] = true;

        // Request update of menu items.
        this.requestUpdate.next();
    }

    // #endregion

    // #region Process Menu Items

    ProcessMenuItems() {
        const self = this;

        // Make a copy of the entries, but setting all to be active unless they appear on the Inactives list.
        const menus = (AllMenuItems || []).map((m) => {
            const item = { ...m, Active: false, Enabled: m.DefaultEnabled, Submenus: [], IsExpanded: false } as NavMenuItem;
            let isItemActive = this.RouteURL ? (m.Route || '').includes(this.RouteURL) : false;

            if (m.Submenus?.length) {
                // Process all child items.
                const children = m.Submenus.map((c) => {
                    const child = { ...c, Active: false, Enabled: c.DefaultEnabled };

                    // Look for an override for this item.
                    const childOver = this.Overrides[c.MenuID];
                    if (childOver != null) child.Enabled = childOver;

                    if ((child.Route || '').includes(this.RouteURL)) {
                        // Found a match on a subitem. Set the button to being active.
                        child.Active = true;
                        isItemActive = true;

                        // Also set the parent expansion button to being expanded (if it isn't, say for reloading)
                        item.IsExpanded = true;
                    }

                    // Lastly, if this item has an application step, set it now.
                    if (child.ApplicationStep != null) self.SetApplicationStepStatus(child);
                    return child;
                });

                item.Submenus = children;
            }

            // Is there an override for this parent item?
            const parOver = this.Overrides[m.MenuID];
            if (parOver != null) item.Enabled = parOver;

            if (isItemActive) {
                item.Active = true;
            }

            return item;
        });

        // Has the available menu options changed? Compile the unique menu IDs for every active menu existing right now.
        const curIDs = (self.CurrentMenuItems || [])
            .filter((f) => f.Enabled)
            .map((f) => [
                `${f.MenuID.toString()}${f.Active ? 'A' : 'I'}`,
                ...(f.Submenus || []).filter((s) => s.Enabled).map((s) => `${s.MenuID.toString()}${s.Active ? 'A' : 'I'}`),
            ])
            .reduce((p, c) => p.concat(...c), [])
            .sort((a, b) => a.localeCompare(b))
            .join(',');
        const newIDs = (menus || [])
            .filter((f) => f.Enabled)
            .map((f) => [
                `${f.MenuID.toString()}${f.Active ? 'A' : 'I'}`,
                ...(f.Submenus || []).filter((s) => s.Enabled).map((s) => `${s.MenuID.toString()}${s.Active ? 'A' : 'I'}`),
            ])
            .reduce((p, c) => p.concat(...c), [])
            .sort((a, b) => a.localeCompare(b))
            .join(',');

        // Now, we need to quickly sync up the expansion for any menu item in the current menu list (if any) to the new one
        // being built.
        self.CurrentMenuItems?.forEach((f) => {
            if (f.IsExpanded) {
                const otherparent = menus.find((o) => o.MenuID === f.MenuID);
                if (otherparent) {
                    // otherparent.IsExpanded = true;
                }
            }
        });

        // Lastly, based upon the current step, we need to see which available step would be next.
        let nextStep: NavMenuItem | null = null;
        let stopLooking = false;
        for (let i = 0; i < menus.length && !stopLooking; ++i) {
            if (menus[i].Active === true) {
                if (!!menus[i].Submenus?.length) {
                    const submenus = menus[i].Submenus || [];

                    // Look inside for the current submenu step that's active (if any), and set the next step to the one after that.
                    for (let j = 0; j < submenus.length && !stopLooking; ++j) {
                        if (submenus[j].Active === true) {
                            // Found the active submenu. Is this submenu completed and/or optional? If so, we need to look for another step
                            // that's enabled after this one, and select the first such as our next step.
                            if (submenus[j].Completed !== NavMenuItemStatus.Incomplete) {
                                const nStep = submenus.find((s, idx) => idx > j && s.Enabled);
                                if (nStep != null) nextStep = nStep;
                            }

                            stopLooking = true;
                            break;
                        }
                    }
                }
            }
        }

        // Update and send out to all listeners the menu updates.
        this.CurrentMenuItems = menus;
        this._MenuItems.next(menus);
        this._NextStep.next(nextStep);
    }

    // #endregion

    // #region Set Application Step Status

    SetApplicationStepStatus(menu: NavMenuItem) {
        const self = this;

        if (!menu || menu.ApplicationStep == null) return;

        if (!self.ApplicationStatus || !self.ApplicationStatus.HasApplication || self.ApplicationStatus.AppProgress == null) {
            // With no application, we disable any application step that isn't Application Status or Personal Information.
            if (![ApplicationStep.ApplicationStatus, ApplicationStep.PersonalInfo].includes(menu.ApplicationStep)) {
                menu.Enabled = false;
            }
            return;
        }

        const step = menu.ApplicationStep;

        // Begin by setting the value to the bit field mapping from the application's progress.
        menu.Completed = !!(self.ApplicationStatus.AppProgress & menu.ApplicationStep)
            ? NavMenuItemStatus.Completed
            : NavMenuItemStatus.Incomplete;

        // Now, certain steps have override conditions which must be specially checked.
        let appRequired = self.ApplicationStatus.AppRequired != null ? self.ApplicationStatus.AppRequired : 0;

        switch (step) {
            case ApplicationStep.ApplicationStatus:
                // The application is incomplete if it hasn't been submitted, it has unsubmitted changes, or the application is not active (hidden),
                // or if the overall flags indicate it is not ready yet.
                menu.Completed =
                    !!self.ApplicationStatus.DateSubmitted &&
                    !self.ApplicationStatus.HasUnsubmittedChanges &&
                    !!self.ApplicationStatus.Active &&
                    (appRequired == null || (appRequired & self.ApplicationStatus.AppProgress) === appRequired)
                        ? NavMenuItemStatus.Completed
                        : NavMenuItemStatus.Incomplete;
                break;
            case ApplicationStep.PersonalInfo:
                // If the applicant is required to have legal name documentation uploaded, and they have not done so, this would mark the personal info
                // step as not completed.
                if ((appRequired & ApplicationStep.LegalNameDocumentation) === ApplicationStep.LegalNameDocumentation) {
                    // Legal name documentation is required for this user. Their progress depends on if they have uploaded it.
                    if (
                        (self.ApplicationStatus.AppProgress & ApplicationStep.LegalNameDocumentation) !==
                        ApplicationStep.LegalNameDocumentation
                    ) {
                        // No matter their earlier result, they do not have the required legal name doc. Mark as not completed.
                        menu.Completed = NavMenuItemStatus.Incomplete;
                    }
                }
                break;
            case ApplicationStep.ApplicationType:
                // If the application is for a teacher transfer, and the personnel group is not '2', mark as incomplete.
                if (self.ApplicationStatus.IsTeacherTransfer && self.User?.PersonnelGroup !== '2')
                    menu.Completed = NavMenuItemStatus.Incomplete;
                break;
            case ApplicationStep.ProfessionalQualifications:
                // Certain application types do not require this section to be completed. For those, we can flip to optional status.
                if (menu.Completed !== NavMenuItemStatus.Completed && self.ApplicationStatus.AppRequired != null) {
                    menu.Completed = !!(self.ApplicationStatus.AppRequired & step)
                        ? NavMenuItemStatus.Incomplete
                        : NavMenuItemStatus.Info;
                }

                break;
            case ApplicationStep.FloridaCertification:
                // Certification is complex. First, to be "complete" the following must all be true:
                // Statement of Eligibility must be clear -AND- Florida Certification must be clear -AND- Out Of State Certification must be clear.
                const certFlags =
                    ApplicationStep.StatementOfEligibility |
                    ApplicationStep.FloridaCertification |
                    ApplicationStep.OutOfStateCertification;
                if ((self.ApplicationStatus.AppProgress & certFlags) === certFlags) {
                    menu.Completed = NavMenuItemStatus.Completed;
                } else {
                    // We only mark this block as incomplete if any of the 3 above are required.
                    menu.Completed =
                        self.ApplicationStatus.AppRequired != null && (self.ApplicationStatus.AppRequired & certFlags) > 0
                            ? NavMenuItemStatus.Incomplete
                            : null;
                }
                break;
            // Some cases need to be either set as complete, incomplete, or cleared entirely based on whether their particular application
            // type requires it. Those are handled in this grouping.
            case ApplicationStep.InstructionalQualifications:
            case ApplicationStep.StudentTeaching:
            case ApplicationStep.ProfessionalSanctions:
            case ApplicationStep.SubjectAreaExams:
            case ApplicationStep.SubjectAreaInterest:
                if (menu.Completed !== NavMenuItemStatus.Completed && self.ApplicationStatus.AppRequired != null) {
                    menu.Completed = !!(self.ApplicationStatus.AppRequired & step) ? NavMenuItemStatus.Incomplete : null;
                }
                break;
            case ApplicationStep.EmploymentHistory:
                // if (self.Application.IsTeacherTransfer === true || !self.Application.WorkHistory?.HasWorkHistory) return;

                // If the person is a teacher transfer and no one work item is marked as Can Contact, flag it.
                if (self.ApplicationStatus.TransferWithoutCanContact === true) {
                    menu.Completed = NavMenuItemStatus.Incomplete;
                }

                // If there's any work history item where the ContactAsMostRecent flag is set, but there's no email provided, flag it.
                else if (self.ApplicationStatus.ContactMostRecentNoEmail === true) {
                    menu.Completed = NavMenuItemStatus.Incomplete;
                }
                break;
            case ApplicationStep.ApplicantSurvey:
                // The applicant survey is either completed or optional.
                if (!!(self.ApplicationStatus.AppProgress & ApplicationStep.ApplicantSurvey)) {
                    // If we are marked as progress met (Completed), further check for the presence of a survey that's been answered (in either way).
                    if (menu.Completed === NavMenuItemStatus.Completed) {
                        if (self.ApplicationStatus.SurveyPCSB26WeeksNonNull !== true) menu.Completed = NavMenuItemStatus.Optional;
                    }
                } else {
                    // The application progress hasn't reached the survey part. Mark it as optional.
                    menu.Completed = NavMenuItemStatus.Optional;
                }
                break;
            case ApplicationStep.TranscriptsUploads:
                // The transcripts step is either completed or optional.
                menu.Completed = !!(self.ApplicationStatus.AppProgress & ApplicationStep.TranscriptsUploads)
                    ? NavMenuItemStatus.Completed
                    : NavMenuItemStatus.Info;
                break;
            case ApplicationStep.ResumesCoverLetters:
                // The resumes/cover letters step is either completed or optional.
                menu.Completed = !!(self.ApplicationStatus.AppProgress & ApplicationStep.ResumesCoverLetters)
                    ? NavMenuItemStatus.Completed
                    : NavMenuItemStatus.Info;
                break;
        }

        // Lastly, some options may require previous steps to be completed before being available/busy. We check those now.
        if (menu.Prerequisites != null) {
            if ((self.ApplicationStatus.AppProgress & menu.Prerequisites) !== menu.Prerequisites) {
                menu.Enabled = false;
            }
        }

        // All instructional steps require the instructional steps to be in the application's available field.
        if (
            [
                ApplicationStep.FloridaCertification,
                ApplicationStep.InstructionalQualifications,
                ApplicationStep.ProfessionalSanctions,
                ApplicationStep.SubjectAreaExams,
                ApplicationStep.SubjectAreaInterest,
                ApplicationStep.StudentTeaching,
            ].includes(menu.ApplicationStep)
        ) {
            const allInst =
                ApplicationStep.SubjectAreaInterest |
                ApplicationStep.StatementOfEligibility |
                ApplicationStep.SubjectAreaExams |
                ApplicationStep.ProfessionalSanctions |
                ApplicationStep.StudentTeaching |
                ApplicationStep.InstructionalQualifications |
                ApplicationStep.FloridaCertification;

            const instAvail = ((self.ApplicationStatus.AppAvailable || 0) & allInst) === allInst;
            menu.Enabled = instAvail;
        }

        // Custodial requires the survey for custodians.
        if (menu.ApplicationStep === ApplicationStep.CustodialSurvey) {
            menu.Enabled = !!((self.ApplicationStatus.AppAvailable || 0) & ApplicationStep.CustodialSurvey);
        }

        // Paras requires the survey for paras.
        if (menu.ApplicationStep === ApplicationStep.ParaSurvey) {
            menu.Enabled = !!((self.ApplicationStatus.AppAvailable || 0) & ApplicationStep.ParaSurvey);
        }

        // Enable/disable teacher transfer related items as necessary based on transfer status.
        const showTransfer =
            !!(
                (self.ApplicationStatus.AppAvailable || 0) &
                (ApplicationStep.ProfessionalQualifications |
                    ApplicationStep.EmploymentHistory |
                    ApplicationStep.FloridaCertification |
                    ApplicationStep.ContactInfo |
                    ApplicationStep.PersonalInfo)
            ) && self.ApplicationStatus.IsTeacherTransfer === true;

        if (showTransfer) {
            // Hide all of the non-default, non-transfer options.
            if (NonTeacherTransferSteps.includes(menu.ApplicationStep)) menu.Enabled = false;

            // Show the transfer options.
            if (TeacherTransferSteps.includes(menu.ApplicationStep)) menu.Enabled = true;
        } else {
            // Hide the transfer menu item.
            if (menu.ApplicationStep === ApplicationStep.TeacherTransfer) menu.Enabled = false;
        }
    }

    // #endregion
}

interface NavMenuAccess {
    MenuID: RouteMenus;
    Enabled: boolean;
}

export interface NavMenuItem {
    Active?: boolean;
    ApplicationStep?: ApplicationStep | null;
    DefaultEnabled: boolean;
    Enabled?: boolean;
    MenuID: RouteMenus;
    MenuText?: string;
    MenuIcon?: string;
    Rights?: SystemRole[];
    Description?: string;
    DisplayAsLaunch?: boolean;
    DisplayAsMenu?: boolean;
    LaunchButtonText?: string;
    LaunchHeader?: string;
    SVGIcon?: boolean;
    IconClasses?: string[];
    IsAbout?: boolean;
    IsExpanded?: boolean;
    IsHome?: boolean;
    IsLogout?: boolean;
    Completed?: NavMenuItemStatus | null;
    Prerequisites?: number | null;

    // Optional
    ButtonIndex?: number;
    Color?: string;
    Exclude?: boolean;
    ExternalURL?: string;
    Route?: RoutePaths;
    Submenus?: NavMenuItem[];
}

export enum NavMenuItemStatus {
    Incomplete = 1,
    Optional = 2,
    Completed = 3,
    Info = 4,
}
